import React, { useEffect, useMemo, useState } from 'react';
import EvalByApplication from '../components/admin/EvalByApplication';
import EvalByEvaluator from '../components/admin/EvalByEvaluator';
import Button from 'shared/components/UC/Button';
import adminService from '../services/adminService';
import { EvalBoardRow, EvalByEvaluatorRow } from '../types/admin';
import styles from './EvaluationsDashboard.module.css';

interface EvaluationsDashboardProps {
  formTemplateId: string | null;
  onViewEvaluations: (applicationId: string) => void;
  /**
   * Abre el tablero directamente en las invitaciones pendientes de envío: la
   * sub-vista por evaluador —la única desde la que se puede enviar— y con el
   * filtro ya aplicado. Lo usa el asistente de importación cuando quedan
   * asignaciones sin invitar.
   */
  focusPendingInvitations?: boolean;
}

type SubTab = 'by_application' | 'by_evaluator';


const EvaluationsDashboard: React.FC<EvaluationsDashboardProps> = ({
  formTemplateId,
  onViewEvaluations,
  focusPendingInvitations = false,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>(
    focusPendingInvitations ? 'by_evaluator' : 'by_application'
  );
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  // ── Por Postulación state ─────────────────────────────────────────────────
  const [boardRows, setBoardRows] = useState<EvalBoardRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ── Por Evaluador state ───────────────────────────────────────────────────
  const [evaluatorRows, setEvaluatorRows] = useState<EvalByEvaluatorRow[]>([]);
  const [evaluatorLoading, setEvaluatorLoading] = useState(false);
  const [evaluatorLoaded, setEvaluatorLoaded] = useState(false);
  // Se incrementa tras enviar invitaciones para recargar ambos tableros:
  // el envío cambia la cobertura por postulación y los conteos por evaluador.
  const [reloadKey, setReloadKey] = useState(0);

  // Load Por Postulación on mount / formTemplateId change
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    adminService
      .getEvaluationsBoardData(formTemplateId)
      .then((data) => {
        if (!cancelled) {
          setBoardRows(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formTemplateId, reloadKey]);

  // Reset evaluator data when formTemplateId changes so it reloads
  useEffect(() => {
    setEvaluatorLoaded(false);
    setEvaluatorRows([]);
  }, [formTemplateId]);

  // Load Por Evaluador on mount (eager) so alert counts are correct from the start
  useEffect(() => {
    if (evaluatorLoaded) return;
    let cancelled = false;
    setEvaluatorLoading(true);
    adminService
      .getEvaluatorBoardData(formTemplateId)
      .then((data) => {
        if (!cancelled) {
          setEvaluatorRows(data);
          setEvaluatorLoading(false);
          setEvaluatorLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setEvaluatorLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [evaluatorLoaded, formTemplateId]);

  // ── Alert counts ──────────────────────────────────────────────────────────
  const alertCounts = useMemo(
    () => ({
      // Por Postulación — coverage breakdown (naturally exclusive, sum = boardRows.length)
      coverageNone:       boardRows.filter((r) => r.coverage === 'none').length,
      coverageInvited:    boardRows.filter((r) => r.coverage === 'invited').length,
      coverageIncomplete: boardRows.filter((r) => r.coverage === 'incomplete').length,
      coverageComplete:   boardRows.filter((r) => r.coverage === 'complete').length,
      // Por Evaluador — cupo breakdown (naturally exclusive, sum = evaluatorRows.length)
      cupoOverloaded: evaluatorRows.filter((r) => r.disponible < 0).length,
      cupoZero:       evaluatorRows.filter((r) => r.disponible === 0).length,
      cupoAvailable:  evaluatorRows.filter((r) => r.disponible > 0).length,
    }),
    [boardRows, evaluatorRows]
  );

  return (
    <div className={styles.container}>
      {/* View switcher — segmented toggle (mutually exclusive) */}
      <div className={styles.viewToggle} role="tablist">
        <span className={styles.viewToggleLabel}>Vista:</span>
        <Button
          type="button"
          role="tab"
          aria-selected={activeSubTab === 'by_application'}
          variant={activeSubTab === 'by_application' ? 'primary' : 'secondary'}
          size="sm"
          className={
            activeSubTab === 'by_application' ? '' : styles.viewToggleInactive
          }
          onClick={() => setActiveSubTab('by_application')}
        >
          Por postulación
        </Button>
        <Button
          type="button"
          role="tab"
          aria-selected={activeSubTab === 'by_evaluator'}
          variant={activeSubTab === 'by_evaluator' ? 'primary' : 'secondary'}
          size="sm"
          className={
            activeSubTab === 'by_evaluator' ? '' : styles.viewToggleInactive
          }
          onClick={() => setActiveSubTab('by_evaluator')}
        >
          Por evaluador
        </Button>
      </div>

      {activeSubTab === 'by_application' && (
        <EvalByApplication
          rows={boardRows}
          isLoading={isLoading}
          onViewEvaluations={onViewEvaluations}
          onToast={showToast}
          toolsHeader={
            <p className={styles.summaryProse}>
              De las <strong>{boardRows.length}</strong> postulaciones admisibles,
              hay <strong>{alertCounts.coverageNone}</strong> sin evaluador,{' '}
              <strong>{alertCounts.coverageInvited}</strong> con invitación,{' '}
              <strong>{alertCounts.coverageIncomplete}</strong> con asignación
              parcial y <strong>{alertCounts.coverageComplete}</strong> con
              asignación completa.
            </p>
          }
        />
      )}

      {activeSubTab === 'by_evaluator' && (
        <EvalByEvaluator
          rows={evaluatorRows}
          isLoading={evaluatorLoading}
          onViewEvaluations={onViewEvaluations}
          onToast={showToast}
          formTemplateId={formTemplateId}
          initialInvitationFilter={focusPendingInvitations}
          onDataChanged={() => {
            setEvaluatorLoaded(false);
            setReloadKey((k) => k + 1);
          }}
          toolsHeader={
            <p className={styles.summaryProse}>
              De los <strong>{evaluatorRows.length}</strong> evaluadores asignados,
              hay <strong>{alertCounts.cupoOverloaded}</strong> sobrecargados,{' '}
              <strong>{alertCounts.cupoZero}</strong> sin cupo,{' '}
              <strong>{alertCounts.cupoAvailable}</strong> con cupo disponible.
            </p>
          }
        />
      )}

      {toast && (
        <div className={styles.toast}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default EvaluationsDashboard;
